import {
  ECONOMY_PERMISSION_RESOURCES,
  NAV_MENU_ITEMS,
  type PermissionAction,
  type PermissionResource,
} from '@/lib/permissions';
import { isNavModuleVisible } from '@/lib/clinicModules';

export function isPatientsSectionPath(pathname: string): boolean {
  return pathname === '/patients' || pathname.startsWith('/patients/');
}

/** Patient record workspace (`/patients/:id`), not the list, new form, inbox, or dental module. */
export function isCommunicationInboxPath(pathname: string): boolean {
  return pathname === '/patients/messages' || pathname === '/patients/kommunikation';
}

export function isPatientRecordWorkspacePath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'patients') return false;
  if (!parts[1] || parts[1] === 'new' || parts[1] === 'messages' || parts[1] === 'kommunikation') {
    return false;
  }
  if (!parts[2]) return true;
  if (parts[2] === 'tand') return false;
  return (
    parts[2] === 'henvisning' ||
    parts[2] === 'korrespondance' ||
    parts[2] === 'ggop' ||
    parts[2] === 'journal'
  );
}

export function isNavItemActive(to: string, pathname: string): boolean {
  if (to.startsWith('/economy')) return pathname.startsWith('/economy');
  if (to === '/patients/messages') return isCommunicationInboxPath(pathname);
  if (to === '/patients')
    return isPatientsSectionPath(pathname) && !isCommunicationInboxPath(pathname);
  return pathname === to || pathname.startsWith(`${to}/`);
}

export type NavBadge = 'unread_messages' | 'unread_communication' | 'open_tasks';

export function navBadgeCount(
  badge: NavBadge | undefined,
  counts: {
    unreadMessages: number;
    openTasks: number;
    unreadUnmatchedEmails: number;
  },
): number {
  if (!badge) return 0;
  switch (badge) {
    case 'unread_messages':
      return counts.unreadMessages;
    case 'unread_communication':
      return counts.unreadMessages + counts.unreadUnmatchedEmails;
    case 'open_tasks':
      return counts.openTasks;
    default: {
      const unexpected: never = badge;
      return unexpected;
    }
  }
}

export interface ClinicNavItemDef {
  to: string;
  i18nKey: string;
  icon:
    | 'home'
    | 'patients'
    | 'messages'
    | 'referrals'
    | 'medcom'
    | 'appointments'
    | 'tasks'
    | 'economy'
    | 'settings';
  moduleKey?: string;
  permissionResource?: PermissionResource;
  badge?: NavBadge;
  mobile: 'primary' | 'more' | 'none';
}

export const CLINIC_NAV_ITEMS: readonly ClinicNavItemDef[] = [
  { to: '/dashboard', i18nKey: 'nav.dashboard', icon: 'home', mobile: 'primary' },
  {
    to: '/patients',
    i18nKey: 'nav.patients',
    icon: 'patients',
    moduleKey: 'patients',
    permissionResource: 'patients',
    mobile: 'primary',
  },
  {
    to: '/patients/messages',
    i18nKey: 'nav.messages',
    icon: 'messages',
    moduleKey: 'patients',
    permissionResource: 'messages',
    badge: 'unread_communication',
    mobile: 'primary',
  },
  {
    to: '/henvisninger',
    i18nKey: 'nav.referrals',
    icon: 'referrals',
    moduleKey: 'referrals',
    permissionResource: 'referrals',
    mobile: 'more',
  },
  {
    to: '/medcom',
    i18nKey: 'nav.medcom',
    icon: 'medcom',
    moduleKey: 'communicate',
    permissionResource: 'patients',
    mobile: 'more',
  },
  {
    to: '/appointments',
    i18nKey: 'nav.appointments',
    icon: 'appointments',
    moduleKey: 'appointments',
    permissionResource: 'appointments',
    mobile: 'primary',
  },
  {
    to: '/tasks',
    i18nKey: 'nav.tasks',
    icon: 'tasks',
    permissionResource: 'tasks',
    badge: 'open_tasks',
    mobile: 'more',
  },
  {
    to: '/economy/invoices',
    i18nKey: 'nav.economy',
    icon: 'economy',
    moduleKey: 'invoices',
    permissionResource: 'invoices',
    mobile: 'more',
  },
  {
    to: '/settings',
    i18nKey: 'nav.settings',
    icon: 'settings',
    permissionResource: 'settings_general',
    mobile: 'more',
  },
];

export const MOBILE_PRIMARY_ORDER = [
  '/dashboard',
  '/patients',
  '/appointments',
  '/patients/messages',
] as const;

export interface NavAccess {
  isPlatformAdmin: boolean;
  clinicId: string | undefined;
  hasPermission: (
    clinicId: string,
    resource: PermissionResource,
    action: PermissionAction,
  ) => boolean;
  activatedModules: Record<string, boolean>;
  modulesLoaded: boolean;
}

export function isClinicNavItemAllowed(item: ClinicNavItemDef, access: NavAccess): boolean {
  if (access.clinicId) {
    const navItem = NAV_MENU_ITEMS.find((nav) => nav.path === item.to);
    if (item.to === '/patients/messages') {
      const canInbox =
        access.hasPermission(access.clinicId, 'nav_messages', 'view') ||
        access.hasPermission(access.clinicId, 'messages', 'view') ||
        access.hasPermission(access.clinicId, 'communication', 'view');
      if (!canInbox) return false;
    } else if (navItem && !access.hasPermission(access.clinicId, navItem.id, 'view')) {
      return false;
    }
    if (item.to === '/economy/invoices') {
      const canEconomy = ECONOMY_PERMISSION_RESOURCES.some((resource) =>
        access.hasPermission(access.clinicId!, resource, 'view'),
      );
      if (!canEconomy) return false;
    } else if (item.to === '/medcom') {
      if (!access.hasPermission(access.clinicId, 'medcom', 'view')) return false;
    } else if (item.to === '/patients/messages') {
      // Visibility already checked above (messages or communication).
    } else if (item.permissionResource) {
      if (!access.hasPermission(access.clinicId, item.permissionResource, 'view')) return false;
    }
  }
  if (item.moduleKey) {
    return isNavModuleVisible(access.activatedModules, item.moduleKey, access.modulesLoaded);
  }
  return true;
}

export function visibleClinicNavItems(access: NavAccess): ClinicNavItemDef[] {
  return CLINIC_NAV_ITEMS.filter((item) => isClinicNavItemAllowed(item, access));
}

export function mobilePrimaryNavItems(items: ClinicNavItemDef[]): ClinicNavItemDef[] {
  return MOBILE_PRIMARY_ORDER.map((path) => items.find((item) => item.to === path)).filter(
    (item): item is ClinicNavItemDef => Boolean(item),
  );
}

export function mobileMoreNavItems(items: ClinicNavItemDef[]): ClinicNavItemDef[] {
  return items.filter((item) => item.mobile === 'more');
}
